// src/TrackBus.jsx
// ✅ RTDB LIVE ONLY (no Firestore live reads)
// - Stop -> marker static (no jitter movement)
// - Move -> smooth fast marker move
// - No blink: no iframe reload, no pulse, no auto-panTo
// ✅ Custom marker icon: bus.jpg

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";

// ✅ RTDB
import { ref, onValue, off } from "firebase/database";
import { rtdb } from "./firebase";

const clamp = (n, a, b) => Math.max(a, Math.min(b, n));

const distanceMeters = (lat1, lng1, lat2, lng2) => {
  const R = 6371000;
  const toRad = (x) => (x * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const aa =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(aa));
};

// --- Google Maps loader (load once) ---
let googleMapsPromise = null;
function loadGoogleMaps(apiKey) {
  if (window.google?.maps) return Promise.resolve();
  if (googleMapsPromise) return googleMapsPromise;

  googleMapsPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-google-maps="1"]');
    if (existing) {
      if (window.google?.maps) return resolve();
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener(
        "error",
        () => reject(new Error("Failed to load Google Maps")),
        { once: true }
      );
      return;
    }

    const s = document.createElement("script");
    s.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&v=weekly`;
    s.async = true;
    s.defer = true;
    s.dataset.googleMaps = "1";
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Failed to load Google Maps"));
    document.head.appendChild(s);
  });

  return googleMapsPromise;
}

// ✅ bus1..bus15 allowed
const isValidBusId15 = (bid) =>
  /^bus([1-9]|1[0-5])$/.test(String(bid || "").toLowerCase());

export default function TrackBus() {
  const { busId } = useParams();
  const [bus, setBus] = useState(null);
  const [err, setErr] = useState("");

  // ✅ Correct env variable (CRA)
  const apiKey = (process.env.REACT_APP_GOOGLE_MAPS_KEY || "").trim();

  // ✅ Put bus.jpg inside: public/bus.jpg
  const BUS_ICON_URL = `${process.env.PUBLIC_URL}/bus.jpg`;

  // Map refs
  const mapDivRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);

  // Animation
  const animRef = useRef(null);

  // Raw point history
  const lastRawRef = useRef(null); // {lat,lng,t}

  // Stable marker position (what we show)
  const stableRef = useRef(null); // {lat,lng,t}

  // Smoothed position while moving (EMA)
  const emaPosRef = useRef(null); // {lat,lng}

  // Mode + hysteresis
  const modeRef = useRef("stopped"); // "stopped" | "moving"
  const moveConfirmRef = useRef(0);
  const stopConfirmRef = useRef(0);

  // speed EMA (estimate from points)
  const speedEmaRef = useRef(0);
  const [speedKmh, setSpeedKmh] = useState(0);

  // Maps state
  const [mapsReady, setMapsReady] = useState(false);
  const [mapsFailed, setMapsFailed] = useState(false);

  // ---- RTDB bus listener (LIVE ONLY) ----
  useEffect(() => {
    if (!busId) return;

    const bid = String(busId).toLowerCase();
    if (!isValidBusId15(bid)) {
      setBus(null);
      setErr("Invalid bus id for LIVE (only bus1..bus15).");
      return;
    }

    setErr("");
    const r = ref(rtdb, `live/${bid}`);

    const unsub = onValue(
      r,
      (snap) => setBus(snap.exists() ? snap.val() : null),
      (e) => setErr(e?.message || "Failed to load live data")
    );

    return () => {
      try {
        unsub();
      } catch (e) {
        try {
          off(r);
        } catch (_) {}
      }
    };
  }, [busId]);

  const updatedMs = useMemo(() => {
    const v = bus?.updatedAtMs;
    const n = typeof v === "number" ? v : Number(v || 0);
    return Number.isFinite(n) ? n : 0;
  }, [bus]);

  const liveNow = useMemo(() => {
    if (!bus) return false;
    if (bus.isLive === false) return false;
    if (!updatedMs) return false;
    return Date.now() - updatedMs < 2 * 60 * 1000;
  }, [bus, updatedMs]);

  const lat = bus?.lat != null ? Number(bus.lat) : null;
  const lng = bus?.lng != null ? Number(bus.lng) : null;
  const acc = bus?.accuracy != null ? Number(bus.accuracy) : null;

  const updatedText = updatedMs ? new Date(updatedMs).toLocaleString() : "—";

  // ✅ DRIVER SPEED from RTDB (prefer speedKmh; fallback speed (m/s))
  const driverSpeedKmh = useMemo(() => {
    const vKmh = bus?.speedKmh;
    if (vKmh != null) {
      const n = Number(vKmh);
      if (Number.isFinite(n)) return clamp(n, 0, 160);
    }

    const vMs = bus?.speed; // m/s
    if (vMs != null) {
      const n = Number(vMs);
      if (Number.isFinite(n)) return clamp(n * 3.6, 0, 160);
    }

    return null;
  }, [bus]);

  const shownSpeedKmh = driverSpeedKmh != null ? driverSpeedKmh : speedKmh;
  const speedLabel =
    driverSpeedKmh != null ? "Speed (driver)" : "Speed (estimate)";

  // ---- Load Maps ----
  useEffect(() => {
    let alive = true;
    setMapsReady(false);
    setMapsFailed(false);

    (async () => {
      if (!apiKey) {
        setMapsFailed(true);
        return;
      }
      try {
        await loadGoogleMaps(apiKey);
        if (!alive) return;
        setMapsReady(true);
        setMapsFailed(false);
      } catch {
        if (!alive) return;
        setMapsReady(false);
        setMapsFailed(true);
      }
    })();

    return () => {
      alive = false;
    };
  }, [apiKey]);

  // ---- Reset on busId ----
  useEffect(() => {
    if (animRef.current) cancelAnimationFrame(animRef.current);
    animRef.current = null;

    mapRef.current = null;
    markerRef.current = null;

    lastRawRef.current = null;
    stableRef.current = null;
    emaPosRef.current = null;

    modeRef.current = "stopped";
    moveConfirmRef.current = 0;
    stopConfirmRef.current = 0;

    speedEmaRef.current = 0;
    setSpeedKmh(0);
  }, [busId]);

  // ---- Init map once ----
  useEffect(() => {
    if (!mapsReady) return;
    if (!mapDivRef.current) return;
    if (mapRef.current) return;

    const center =
      lat != null && lng != null
        ? { lat, lng }
        : { lat: 8.247407, lng: 77.547264 };

    const map = new window.google.maps.Map(mapDivRef.current, {
      center,
      zoom: 18,
      mapTypeId: "roadmap",
      streetViewControl: false,
      fullscreenControl: true,
      mapTypeControl: false,
      clickableIcons: false,
      gestureHandling: "greedy",
      rotateControl: false,
      tilt: 0,
    });

    // ✅ Custom icon (bus.jpg)
    const ICON_SIZE = 46;
    const icon = {
      url: BUS_ICON_URL,
      scaledSize: new window.google.maps.Size(ICON_SIZE, ICON_SIZE),
      anchor: new window.google.maps.Point(ICON_SIZE / 2, ICON_SIZE / 2),
    };

    const marker = new window.google.maps.Marker({
      map,
      position: center,
      title: busId ? busId.toUpperCase() : "BUS",
      optimized: true,
      icon,
    });

    mapRef.current = map;
    markerRef.current = marker;

    const now = Date.now();
    stableRef.current = { ...center, t: now };
    emaPosRef.current = { ...center };
    lastRawRef.current = { ...center, t: now };
  }, [mapsReady, lat, lng, busId, BUS_ICON_URL]);

  // ---- Core: update marker (STOP=HARD STATIC, MOVE=SMOOTH) ----
  useEffect(() => {
    if (!mapsReady) return;
    if (!markerRef.current) return;
    if (lat == null || lng == null) return;

    // ✅ prefer updatedMs (2 sec once from driver)
    let now = updatedMs || Date.now();
    const prevRaw0 = lastRawRef.current;
    if (prevRaw0?.t != null && now <= prevRaw0.t) now = prevRaw0.t + 1;

    const raw = { lat, lng, t: now };

    // init raw
    if (!lastRawRef.current) lastRawRef.current = raw;

    // init stable
    if (!stableRef.current) {
      stableRef.current = raw;
      emaPosRef.current = { lat, lng };
      markerRef.current.setPosition({ lat, lng });
      return;
    }

    const prevRaw = lastRawRef.current;
    const stable = stableRef.current;

    const dRaw = distanceMeters(prevRaw.lat, prevRaw.lng, raw.lat, raw.lng);
    const dStable = distanceMeters(stable.lat, stable.lng, raw.lat, raw.lng);

    const dt = Math.max(500, raw.t - prevRaw.t);

    // accuracy default
    const accuracy = Number.isFinite(acc) && acc > 0 ? acc : 25;

    // ✅ IMPORTANT: lower thresholds so slow bus also moves (2 sec interval)
    const MOVE_DIST = clamp(accuracy * 0.35, 3, 8);   // enter move
    const STOP_DIST = clamp(accuracy * 0.55, 5, 12);  // stop/freeze window

    // speed estimate (only for display + optional help)
    let instKmh = clamp((dRaw / dt) * 3.6, 0, 130);
    if (dRaw < MOVE_DIST) instKmh = 0;

    const spPrev = speedEmaRef.current || 0;
    const sp = spPrev * 0.72 + instKmh * 0.28;
    speedEmaRef.current = sp < 0.9 ? 0 : sp;
    setSpeedKmh(speedEmaRef.current);

    lastRawRef.current = raw;

    // movement decision (distance based => reliable)
    const movingSignal = dStable >= MOVE_DIST;
    const stoppedSignal = dStable <= STOP_DIST;

    // ---- mode switch with hysteresis ----
    if (modeRef.current === "stopped") {
      if (movingSignal) moveConfirmRef.current += 1;
      else moveConfirmRef.current = 0;

      if (moveConfirmRef.current >= 2) {
        modeRef.current = "moving";
        moveConfirmRef.current = 0;
        stopConfirmRef.current = 0;
      } else {
        // STOP MODE: HARD FREEZE marker
        if (animRef.current) cancelAnimationFrame(animRef.current);
        animRef.current = null;
        return;
      }
    } else {
      if (stoppedSignal) stopConfirmRef.current += 1;
      else stopConfirmRef.current = 0;

      if (stopConfirmRef.current >= 3) {
        modeRef.current = "stopped";
        stopConfirmRef.current = 0;
        moveConfirmRef.current = 0;

        if (animRef.current) cancelAnimationFrame(animRef.current);
        animRef.current = null;

        // keep stable where it last was (freeze)
        return;
      }
    }

    // ---- MOVING: smooth update ----
    const from = { lat: stable.lat, lng: stable.lng };
    const toRaw = { lat: raw.lat, lng: raw.lng };

    // alpha based on speed if available (driver speed preferred)
    const motionSpeedKmh =
      driverSpeedKmh != null ? driverSpeedKmh : speedEmaRef.current || 0;

    const alpha = clamp(0.20 + motionSpeedKmh / 80, 0.25, 0.65);

    const emaPrev = emaPosRef.current || toRaw;
    const emaNext = {
      lat: emaPrev.lat + (toRaw.lat - emaPrev.lat) * alpha,
      lng: emaPrev.lng + (toRaw.lng - emaPrev.lng) * alpha,
    };
    emaPosRef.current = emaNext;

    const moved = distanceMeters(from.lat, from.lng, emaNext.lat, emaNext.lng);

    // big jump safety
    if (moved > 300) {
      markerRef.current.setPosition(emaNext);
      stableRef.current = { ...emaNext, t: now };
      return;
    }

    const duration = clamp(180 + moved * 10, 220, 700);
    const start = performance.now();

    if (animRef.current) cancelAnimationFrame(animRef.current);

    const step = (t) => {
      const p = Math.min((t - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);

      const cur = {
        lat: from.lat + (emaNext.lat - from.lat) * eased,
        lng: from.lng + (emaNext.lng - from.lng) * eased,
      };

      try {
        markerRef.current.setPosition(cur);
      } catch {}

      if (p < 1) animRef.current = requestAnimationFrame(step);
      else stableRef.current = { ...emaNext, t: now };
    };

    animRef.current = requestAnimationFrame(step);

    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
      animRef.current = null;
    };
  }, [mapsReady, lat, lng, acc, updatedMs, driverSpeedKmh]);

  const openGoogleMaps = () => {
    if (lat == null || lng == null) return;
    window.open(`https://www.google.com/maps?q=${lat},${lng}`, "_blank");
  };

  const recenter = () => {
    if (!mapsReady || !mapRef.current) return;
    const s = stableRef.current;
    if (!s) return;
    mapRef.current.panTo({ lat: s.lat, lng: s.lng });
  };

  const mapModeText = mapsReady
    ? "Google Maps JS (smooth)"
    : mapsFailed
    ? "Maps load failed (check API key restrictions)"
    : "Loading Google Maps...";

  return (
    <div className="track-page">
      <style>{`
        .track-page{
          min-height:100vh;
          padding: 88px 16px 34px;
          background: linear-gradient(180deg, #060a13 0%, #0b1220 100%);
        }
        .track-shell{ max-width: 980px; margin: 0 auto; }
        .glass-pro{
          border-radius: 22px;
          background: linear-gradient(180deg, rgba(255,255,255,.13), rgba(255,255,255,.06));
          border: 1px solid rgba(255,255,255,.18);
          box-shadow: 0 22px 70px rgba(0,0,0,.42);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          overflow: hidden;
          color: #fff;
        }
        .t-title{ color: rgba(255,255,255,.95); }
        .t-sub{ color: rgba(234,240,255,.72); font-size: 13px; line-height: 1.6; }
        .pill{
          border: 1px solid rgba(255,255,255,.14);
          background: rgba(255,255,255,.07);
          border-radius: 14px;
          padding: 12px 12px;
          color: rgba(255,255,255,.90);
        }
        .muted{ color: rgba(234,240,255,.70); font-size: 12px; }
        .hr-soft{ border-color: rgba(255,255,255,.12); }
        .status-pill{
          display:inline-flex; align-items:center; gap:8px;
          padding: 6px 10px; border-radius: 12px;
          font-weight: 900; letter-spacing: .6px;
        }
        .status-live{
          background: rgba(21,199,119,.16);
          border: 1px solid rgba(21,199,119,.35);
        }
        .status-off{
          background: rgba(255,77,109,.16);
          border: 1px solid rgba(255,77,109,.35);
        }
        .map-wrap{
          border-radius: 18px;
          overflow: hidden;
          border: 1px solid rgba(255,255,255,.14);
          background: rgba(0,0,0,.10);
          position: relative;
          height: 520px;
        }
        .btn-pro{
          border-radius: 14px !important;
          font-weight: 900 !important;
          padding: 10px 14px !important;
        }
      `}</style>

      <div className="track-shell">
        <div className="glass-pro p-4">
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
            <div>
              <h3 className="t-title fw-bold m-0">
                {busId?.toUpperCase()} Tracking
              </h3>
              <div className="t-sub mt-1">{mapModeText}</div>
            </div>

            <Link className="btn btn-outline-light btn-sm btn-pro" to="/?buses=1">
              Back to Buses
            </Link>
          </div>

          <hr className="hr-soft" />

          {err ? <div className="alert alert-danger">{err}</div> : null}

          <div className="row g-3 mb-3">
            <div className="col-12 col-md-4">
              <div className="pill">
                <div className="muted">Status</div>
                <div className="mt-1">
                  <span
                    className={`status-pill ${
                      liveNow ? "status-live" : "status-off"
                    }`}
                  >
                    {liveNow ? "LIVE" : "OFFLINE"}
                  </span>
                </div>
              </div>
            </div>

            <div className="col-12 col-md-4">
              <div className="pill">
                <div className="muted">{speedLabel}</div>
                <div className="fw-bold mt-1">
                  {Math.round(shownSpeedKmh)} km/h
                </div>
              </div>
            </div>

            <div className="col-12 col-md-4">
              <div className="pill">
                <div className="muted">Last Updated</div>
                <div className="fw-bold mt-1">{updatedText}</div>
              </div>
            </div>
          </div>

          <div className="map-wrap mb-3">
            {mapsReady ? (
              <div ref={mapDivRef} style={{ width: "100%", height: "100%" }} />
            ) : (
              <div className="p-3" style={{ color: "rgba(234,240,255,.80)" }}>
                Loading Google Maps...
              </div>
            )}
          </div>

          <div className="d-flex gap-2 flex-wrap">
            <button
              className="btn btn-success btn-pro"
              onClick={openGoogleMaps}
              disabled={lat == null || lng == null}
            >
              Open in Google Maps
            </button>
            <button className="btn btn-outline-light btn-pro" onClick={recenter}>
              Recenter to Bus
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}